/**
 * AuthGuard Component
 *
 * Protects routes that require authentication.
 * All authentication is via Central Identity.
 *
 * IMPORTANT: Does NOT redirect during workspace switch.
 * isSwitchingWorkspace suppresses the login redirect so that
 * a transient auth-state change (e.g. from a race with the
 * reauth middleware) cannot bounce the user to Central login.
 */

import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  selectIsCentralAuthenticated,
  selectIsCentralInitialized,
  selectIsSwitchingWorkspace,
} from '../../store/slices/centralAuthSlice';

interface AuthGuardProps {
  children?: React.ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const isAuthenticated = useSelector(selectIsCentralAuthenticated);
  const isInitialized = useSelector(selectIsCentralInitialized);
  const isSwitching = useSelector(selectIsSwitchingWorkspace);
  const location = useLocation();

  // Show loading spinner while checking auth state
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto" />
          <p className="mt-4 text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated.
  // BUT: never redirect while a workspace switch is in progress —
  // the reauth middleware may transiently clear isAuthenticated
  // and we must not race it into a login redirect.
  if (!isAuthenticated && !isSwitching) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children ?? <Outlet />}</>;
}
